<script setup lang="ts">
import { AI_ACCOUNT_NOTICE, SIGN_UP_PLAN_LINE } from '@zerohuman/shared'
import { Button, ZhPanel, ZhSystemLabel } from '@zerohuman/ui'
import { onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import {
    type AiAccountExitReason,
    type AiAccountFailure,
    type AiAccountProvider,
    api,
    type FoundedEnterprise,
    type OnboardingStateView,
} from '@/api'
import AiAccountCard from '@/components/AiAccountCard.vue'
import FoundedEnterpriseNotice from '@/components/FoundedEnterpriseNotice.vue'
import { AGENT_SUBSCRIPTIONS, type SubscriptionProvider } from '@/lib/agents'
import { setAiAccountStatus } from '@/lib/aiAccountGuard'
import { reportClaudeOAuthBlocked } from '@/lib/claudeOAuthReport'
import { dismissFoundedNotice, foundedNoticeDismissed } from '@/lib/foundedNotice'
import { AFTER_ACCOUNT_STEP_PATH, onboardingGate, SETUP_CHAT_PATH, setOnboardingGate } from '@/lib/onboardingGate'
import { setupIsOpen } from '@/lib/setupChat'

/**
 * The AI account step of onboarding (#1716): a screen of its own, before the COO's first message, where a new user
 * connects the AI account every later step runs on. A new owner lands here once, straight after sign-up (#1796), and
 * can browse away from it; once the server has stored an account, the COO's conversation is next.
 *
 * Connect Claude Pro/Max (#1719) is the Agents page's own Claude connection: the same `startClaudeOAuth` and
 * `completeClaudeOAuth` calls, for the enterprise layer, with no new connection and none to replace (an upsert, as a
 * key connected here is). A ChatGPT or SuperGrok plan is connected the same way, through that provider's own
 * sign-in. The provider's page opens without `noopener`, so a blocked pop-up can be told from one that
 * opened, and the opener link is cut on the window that comes back. A blocked pop-up is the one failure the server
 * cannot see, so only that one is reported from here; a failed start or complete is recorded by the server.
 *
 * Opening the step tells the server this person reached it (#1718), so leaving without connecting is remembered and
 * they get one reminder with the 2-minute guide a day later. Like the read below, it names no path, so the
 * enterprise keeps the one it is on. It is never waited on: the card works either way.
 *
 * It is the one place either setup path shows the step, so it is also where the step's drop-off is measured (#1770):
 * - Opening it reads setup as this step's own screen, which is how the server records the step shown to this person.
 *   The screen is shared by both paths and does not know which one the person is on, so the read names no path and
 *   never logs a resume; the funnel splits by the person's own setup path.
 * - A founder not connecting can say why, from a closed choice with no free text, and the server records it as their
 *   exit. Neither ever gets in the way: a read or an exit that fails changes nothing on the screen.
 *
 * It is also the screen a sign-in that founded a new enterprise lands on (#2120), so it is where that sign-in is told
 * so (#2218): the session read says whether this session's own sign-in founded the enterprise, and only then does the
 * notice show, until it is dismissed for this browser session. No other screen shows it.
 */
const router = useRouter()
const busy = ref(false)
const failure = ref<AiAccountFailure | null>(null)
/** The authorization under way: whose it is, the provider's page, and the state its answer is completed against. */
const authorization = ref<{ provider: SubscriptionProvider; url: string; state: string } | null>(null)

/** What each provider's plan is called in a sentence, and what its sign-in hands back to paste. */
const PLAN_WORDS: Record<SubscriptionProvider, { plan: string; pasted: string }> = {
    claude: { plan: 'Claude plan', pasted: 'code' },
    openai: { plan: 'ChatGPT plan', pasted: 'address' },
    grok: { plan: 'SuperGrok plan', pasted: 'code' },
}

/** What the card says for each authorization failure: never the code, the state or the server's own error text. */
function popupBlocked(provider: SubscriptionProvider): AiAccountFailure {
    return {
        reason: 'authorization',
        message: `Your browser blocked the window for ${AGENT_SUBSCRIPTIONS[provider].vendor}'s sign-in page. Open the page with the link above, try again, or use an API key instead.`,
    }
}
function startFailed(provider: SubscriptionProvider): AiAccountFailure {
    return {
        reason: 'authorization',
        message: `We couldn't start connecting your ${PLAN_WORDS[provider].plan}. Try again, or use an API key instead.`,
    }
}
function completeFailed(provider: SubscriptionProvider): AiAccountFailure {
    const { plan, pasted } = PLAN_WORDS[provider]
    return {
        reason: 'authorization',
        message: `That ${pasted} didn't connect your ${plan}. It may have expired or been copied in part. Try again, or use an API key instead.`,
    }
}

/** Setup as this step's own read found it on opening (#1770); null until it answers, or when it failed. */
const setup = ref<OnboardingStateView | null>(null)

/**
 * The account is connected: the gate lets go before the move, so the guard does not hold the person on this step,
 * and the status is recorded at once (#1796), so the banner goes and actions run with no further read.
 *
 * While setup is open, the next step is the setup conversation with the COO (#1742, O1): there is no choice of path.
 * Once setup is complete or skipped, or when the read could not say, it is the chat as before, so no setup
 * conversation is opened on a guess.
 */
async function connected(): Promise<void> {
    setOnboardingGate({ ...onboardingGate(), accountConnected: true })
    setAiAccountStatus('connected')
    await router.push(setupIsOpen(setup.value) ? SETUP_CHAT_PATH : AFTER_ACCOUNT_STEP_PATH)
}

/** The step's closed exit choice (#1770), in the order offered. */
const EXIT_CHOICES: ReadonlyArray<{ reason: AiAccountExitReason; label: string }> = [
    { reason: 'no_account', label: "I don't have an account" },
    { reason: 'thought_model_included', label: 'I thought a model was included' },
    { reason: 'other', label: 'Something else' },
]

/** An exit is being sent. */
const leaving = ref(false)
/** The founder has said why they are not connecting: the choice is put away and thanked for. */
const left = ref(false)

/** #2218: the enterprise this session's own sign-in founded, while its notice is shown; null otherwise. */
const founded = ref<FoundedEnterprise | null>(null)

/** Puts the notice away, for the rest of this browser session. */
function dismissFounded(): void {
    const shown = founded.value
    if (shown) {
        dismissFoundedNotice(shown.id)
    }
    founded.value = null
}

/** The notice's Sign out is under way: its button is disabled and says so, so a second click sends nothing. */
const signingOut = ref(false)
/** The notice's Sign out did not go through, so the session may still be open: the notice says so. */
const signOutFailed = ref(false)

/**
 * The notice's Sign out: the shell's own sign-out (`api.logout`), then a full load of the portal, so it starts again
 * from the sign-in screen with nothing of this enterprise left on the page. Only an answer the server accepted counts:
 * one it refused, or one that never arrived, leaves the person on this screen, told so and free to try again.
 */
async function signOut(): Promise<void> {
    if (signingOut.value) {
        return
    }
    signingOut.value = true
    signOutFailed.value = false
    let signedOut = false
    try {
        signedOut = (await api.logout()).ok
    } catch {
        signedOut = false
    }
    if (signedOut) {
        // The button stays disabled while the portal reloads.
        window.location.assign('/')
        return
    }
    signOutFailed.value = true
    signingOut.value = false
}

/**
 * Reads whether this session's own sign-in founded the enterprise, and shows the notice unless it was put away in this
 * browser session. Never waited on: a read that fails, however it fails, shows no notice and changes nothing else.
 */
async function readFounded(): Promise<void> {
    try {
        const fromSignIn = (await api.me())?.foundedEnterprise ?? null
        founded.value = fromSignIn && !foundedNoticeDismissed(fromSignIn.id) ? fromSignIn : null
    } catch {
        founded.value = null
    }
}

onMounted(() => {
    void readFounded()
    void api.reachAiAccountStep()
    // The screen never waits on it, and a refused or failed read shows nothing. What it answers only says where
    // connecting the account leads (`connected`).
    api.onboardingAtAiAccountStep()
        .then(state => {
            setup.value = state
        })
        .catch(() => undefined)
})

async function connect(account: { provider: AiAccountProvider; apiKey: string }): Promise<void> {
    busy.value = true
    failure.value = null
    try {
        const result = await api.connectAiAccount(account)
        if (!result.ok) {
            failure.value = { reason: result.reason, message: result.message }
            return
        }
        await connected()
    } finally {
        busy.value = false
    }
}

async function startSubscription(provider: SubscriptionProvider = 'claude'): Promise<void> {
    busy.value = true
    failure.value = null
    authorization.value = null
    // Opened before the start request, while the click still counts as the person's own: Safari, and every browser on
    // iOS, blocks a window opened after an awaited request. It stays blank until the start answers, then goes to
    // the provider's page. Still no `noopener`, so a blocked pop-up comes back null; the opener link is cut at once.
    const popup = window.open('', '_blank')
    if (popup) {
        popup.opener = null
    }
    let started: { authorizationUrl: string; state: string }
    try {
        started =
            provider === 'claude'
                ? await api.startClaudeOAuth('enterprise')
                : await api.startSubscriptionOAuth(provider, 'enterprise')
    } catch {
        popup?.close()
        // The server records a failed start, so a blocked pop-up on the same attempt is not reported as well.
        failure.value = startFailed(provider)
        return
    } finally {
        busy.value = false
    }
    // Set whether or not the window opened: the card then links to the provider's page and takes what is pasted.
    authorization.value = { provider, url: started.authorizationUrl, state: started.state }
    if (!popup) {
        failure.value = popupBlocked(provider)
        // Telemetry only: a report that does not arrive changes nothing for the person on the card.
        reportClaudeOAuthBlocked().catch((): void => undefined)
        return
    }
    popup.location = started.authorizationUrl
}

async function completeSubscription(code: string): Promise<void> {
    const started = authorization.value
    if (!started) {
        return
    }
    busy.value = true
    failure.value = null
    try {
        const result =
            started.provider === 'claude'
                ? await api.completeClaudeOAuth(code, started.state)
                : await api.completeSubscriptionOAuth(started.provider, code, started.state)
        if (!result.ok) {
            authorization.value = null
            failure.value = completeFailed(started.provider)
            return
        }
        authorization.value = null
        await connected()
    } catch {
        authorization.value = null
        failure.value = completeFailed(started.provider)
    } finally {
        busy.value = false
    }
}

/**
 * Sends the founder's reason. The choice is disabled while it goes and put away after, and the server keeps only a
 * person's first exit. Whether the server took it or not, they are thanked: recording it is ours to get right.
 */
async function leave(reason: AiAccountExitReason): Promise<void> {
    leaving.value = true
    try {
        await api.leaveAiAccountStep(reason)
    } finally {
        leaving.value = false
        left.value = true
    }
}
</script>

<template>
	<section class="flex w-full flex-col gap-4 p-6" data-testid="onboarding-ai-account">
		<!-- #2218: first on the screen a founding sign-in lands on, before anything about the step. -->
		<FoundedEnterpriseNotice
			v-if="founded"
			:founded="founded"
			:signing-out="signingOut"
			:sign-out-failed="signOutFailed"
			@sign-out="signOut"
			@dismiss="dismissFounded"
		/>
		<ZhSystemLabel tone="muted">Step 1 · AI account</ZhSystemLabel>
		<h1 class="text-xl font-semibold">Connect your AI account</h1>
		<p class="text-muted-foreground text-sm">
			Nothing in your enterprise can run until it has an AI account to run on. Connect one here and your COO will
			take it from there.
		</p>
		<!--
			#1933: the notice that the team runs on the person's own AI account, moved here from the sign-in screen. The
			wording is the shared one (the team proposal says it too), in the variant for this step: no "connect it
			next", because this is where it gets connected.
		-->
		<ZhPanel as="aside" aria-label="Your AI account" data-testid="ai-account-notice">
			<p class="text-sm leading-relaxed">{{ AI_ACCOUNT_NOTICE.accountStep }}</p>
		</ZhPanel>
		<!--
			#1741: right after the notice, the Free plan in one line (the COO plus the teammates it includes), so nobody
			reads the COO as a seat on top of the plan. It sat on the sign-up screen until #1933 kept that screen clear of
			plan copy (CTO decision 17), and came here with the notice. The wording is the shared one.
		-->
		<ZhPanel as="aside" aria-label="Your plan" data-testid="sign-up-plan-line">
			<p class="text-sm leading-relaxed">{{ SIGN_UP_PLAN_LINE }}</p>
		</ZhPanel>
		<AiAccountCard
			:failure="failure"
			:busy="busy"
			:authorization-url="authorization?.url ?? null"
			:authorization-for="authorization?.provider ?? null"
			@connect="connect"
			@start-subscription="startSubscription"
			@complete-subscription="completeSubscription"
		/>
		<ZhPanel data-testid="ai-account-exit" class="space-y-3">
			<ZhSystemLabel tone="muted">Not connecting an account?</ZhSystemLabel>
			<p v-if="left" role="status" class="text-sm" data-testid="ai-account-exit-thanks">
				Thanks for telling us. You can come back and connect an account here whenever you're ready.
			</p>
			<template v-else>
				<p class="text-sm">Tell us why. It helps us make this step easier.</p>
				<div class="flex flex-wrap gap-2" role="group" aria-label="Why you are not connecting an account">
					<Button
						v-for="choice in EXIT_CHOICES"
						:key="choice.reason"
						type="button"
						variant="outline"
						:data-exit-reason="choice.reason"
						:disabled="leaving"
						@click="leave(choice.reason)"
					>
						{{ choice.label }}
					</Button>
				</div>
			</template>
		</ZhPanel>
	</section>
</template>
